// Core Libraries
import { Redirect, Route, Switch } from "react-router-dom";
import { useSelector } from "react-redux";

// Authentication
import LoginPage from "components/v2/Pages/LoginPage";
import LoginCallbackPage from "components/v2/Pages/LoginPage/LoginCallbackPage";
import ScrapeApiPage from "components/v2/Pages/LoginPage/ScrapeApiPage";
import LogoutPage from "components/v2/Pages/LogoutPage";

// Onboarding and Welcome
import WelcomePage from "components/v3/pages/onboarding/WelcomePage";
import UnpaidInviteWelcomePage from "components/v3/pages/onboarding/UnpaidInviteWelcomePage";
import PaidInviteWelcomePage from "components/v3/pages/onboarding/PaidInviteWelcomePage";
import GenomicLifeCompleteProductPage from "components/v3/pages/onboarding/GenomicLifeCompleteProductPage";
import RegistrationInstructions from "components/v3/pages/onboarding/RegistrationInstructions";
import GettingStartedPage from "components/v3/pages/onboarding/GettingStartedPage";
import TermsPage from "components/v3/pages/onboarding/TermsPage";
import ProfilePage from "components/v3/pages/onboarding/ProfilePage";
import UnpaidInviteProfilePage from "components/v3/pages/onboarding/UnpaidInviteProfilePage";
import UnpaidInvitePaymentPage from "components/v3/pages/onboarding/UnpaidInvitePaymentPage";

// Dashboard
import DashboardPage from "components/v3/pages/DashboardPage";

// Account Management
import AccountManagementPage from "components/v3/pages/account/AccountManagementPage";
import MFAPage from "components/v3/pages/account/MFAPage";
import MemberInfoPage from "components/v3/pages/account/MemberInfoPage";
import PrivacySecurityPage from "components/v3/pages/account/PrivacySecurityPage";
import TermsOfServicePage from "components/v3/pages/account/TermsOfServicePage";
import PlanInformationPage from "components/v3/pages/account/PlanInformationPage";
import GiftMembershipPage from "components/v3/pages/account/GiftMembershipPage";
import GiftRecipientsPage from "components/v3/pages/account/GiftRecipientsPage";
import GiftPaymentPage from "components/v3/pages/account/GiftPaymentPage";
import ChangePasswordPage from "components/v3/pages/account/ChangePasswordPage";

// Survey Pages
import PrecisionGuidanceSurveyPage from "components/v3/pages/PrecisionGuidanceSurveyPage";
import KitRequestSurveyPage from "components/v3/pages/KitRequestSurveyPage";
import CancerSurveyPage from "components/v3/pages/CancerSurveyPage";
import OrderKitSurveyPage from "components/v3/pages/orderKit/OrderKitSurveyPage";

// Kit Ordering
import OrderingPage from "components/pages/OrderingPage";
import OrderKitConclusionPage from "components/pages/OrderingPage/OrderKitConclusionPage";
import TestKitSelectionPage from "components/v2/Pages/TestKitSelectionPage";

// Reporting and Results
import TestResultsPage from "components/v2/Pages/TestResultsPage";
import TestResultDocumentPage from "components/v2/Pages/TestResultDocumentPage";
import ResultsMoreInfoSlidePage from "components/v2/Pages/ResultsMoreInfoSlidePage";

// CMS Controlled Pages
import HelpCenterPage from "components/v2/CMSDriven/Pages/HelpCenterPage";

// Utility Pages
import MaintenancePage from "components/v2/Pages/MaintenancePage";
import LoggedOutPage from "components/v2/Pages/LoggedOutPage";
import IdleTimer from "components/SessionTimer/IdleTimer";

// Utility and Constants
import { PATHS } from "./constants/pathConstants";
import { FORM_IDS } from "./constants/v3/formConstants";
import { getCookie } from "utils/v3/CookieUtil";
import { validateToken } from "./utils/utils";

// Redux Selectors
import {
  selectUserHasAcceptedAllCurrentAgreements,
  selectUserHasNeverAcceptedSomeAgreements
} from "./store/reducers/formTemplates/FormTemplatesSlice";
import {
  selectGreatInitialApiScrapeIsComplete,
  selectUserPersonalInformation,
  selectHasNewResultsToView
} from "./store/reducers/user/UserAccountSlice";
import ScrollToTop from "components/ScrollToTop";
import AgreementReviewPage from "components/v3/pages/account/AgreementReviewPage";
import ResultsSlidesPage from "components/v2/Pages/ResultsSlidesPage";
import UpdateAgreementsPage from "components/v3/pages/UpdateAgreementsPage";
import MembershipPaymentPage from "components/v3/pages/account/MembershipPaymentPage";
import PaymentReceiptPage from "components/v3/pages/account/PaymentReceiptPage";
import ExplanationOfBenefitsPage from "components/v3/pages/account/ExplanationOfBenefitsPage";
import CancelMembershipPage from "components/v3/pages/account/CancelMembershipPage";
import ShareGenomicLifeInvitePage from "components/v3/pages/account/ShareGenomicLifeInvitePage";
export const PAGES_ALLOWED_TO_UNAUTHENTICATED_USERS = [
  PATHS.MAINTENANCE,
  PATHS.ONBOARDING_WELCOME,
  PATHS.ONBOARDING_WELCOME_UNPAID_INVITE,
  PATHS.ONBOARDING_WELCOME_PAID_INVITE,
  PATHS.ONBOARDING_REGISTRATION_INSTRUCTIONS,
  PATHS.LOGIN,
  PATHS.LOGIN_CALLBACK,
  PATHS.API_SCRAPE,
  PATHS.LOG_OUT,
  PATHS.LOGGED_OUT
];

export const PAGES_FORBIDDEN_TO_LOGGED_IN_USERS = [
  PATHS.ONBOARDING_WELCOME,
  PATHS.ONBOARDING_WELCOME_UNPAID_INVITE,
  PATHS.ONBOARDING_WELCOME_PAID_INVITE
];

const PAGES_FORBIDDEN_TO_INACTIVE_USERS = [
  PATHS.ORDER_KIT,
  PATHS.ORDER_KIT_SELECTION,
  PATHS.ORDER_KIT_CONFIRMATION,
  PATHS.GETTING_STARTED_SURVEY,
  PATHS.CANCER_SURVEY
];

const TERMS_AND_CONDITION_PAGES = [
  PATHS.ONBOARDING_GETTING_STARTED,
  PATHS.ONBOARDING_TERMS,
  PATHS.ONBOARDING_PROFILE,
  PATHS.ONBOARDING_UNPAID_INVITE_PROFILE,
  PATHS.ONBOARDING_UNPAID_INVITE_PAYMENT,
  PATHS.ACCOUNT_MANAGEMENT_UPDATE_AGREEMENTS,
  PATHS.LOG_OUT
];

const ONBOARDING_GETTING_STARTED_REDIRECT_EXEMPT_PATHNAMES = [
  ...TERMS_AND_CONDITION_PAGES,
  PATHS.ONBOARDING_WELCOME_UNPAID_INVITE,
  PATHS.ONBOARDING_WELCOME_PAID_INVITE,
  PATHS.ONBOARDING_GENOMIC_LIFE_COMPLETE_PRODUCT
];

const AppRouter = (props) => {
  const { REACT_APP_UNDER_MAINTENANCE } = process.env;
  const hasNewResultsToView = useSelector(selectHasNewResultsToView);
  const visitorIsAMember = !!useSelector(selectUserPersonalInformation)
    .glMemberId;

  const isMemberActive = useSelector(
    selectUserPersonalInformation
  ).isMemberActive;

  const userNeedsToBeOnboarded = useSelector(
    selectUserHasNeverAcceptedSomeAgreements
  );

  const allAgreementsCurrent = useSelector(
    selectUserHasAcceptedAllCurrentAgreements
  );

  const greatInitialApiScrapeIsComplete = useSelector(
    selectGreatInitialApiScrapeIsComplete
  );

  const sessionIsValid = validateToken(getCookie("accessToken"));

  const rawPathname = window.location.pathname;
  const pathname =
    rawPathname.length > 1 && rawPathname.endsWith("/")
      ? rawPathname.slice(0, -1)
      : rawPathname;

  // There are a few possible states related to authentication here,
  //  and each must be re-renderable as many times as is needed, since
  //  there are multiple dispatches and redirects in the process.
  // These tests are the gatekeepers. This will need refactoring if it
  //  gets much more complicated.

  if (REACT_APP_UNDER_MAINTENANCE && pathname !== PATHS.MAINTENANCE) {
    return <Redirect to={PATHS.MAINTENANCE} />;
  }

  if (
    (!sessionIsValid || !greatInitialApiScrapeIsComplete) &&
    !PAGES_ALLOWED_TO_UNAUTHENTICATED_USERS.includes(pathname)
  ) {
    return <Redirect to={PATHS.LOG_OUT} />;
  }

  if (sessionIsValid && !visitorIsAMember) {
    //return <Redirect to={PATHS.LOG_OUT} />;
  }

  if (
    sessionIsValid &&
    greatInitialApiScrapeIsComplete &&
    userNeedsToBeOnboarded &&
    !ONBOARDING_GETTING_STARTED_REDIRECT_EXEMPT_PATHNAMES.includes(pathname)
  ) {
    return <Redirect to={PATHS.ONBOARDING_GETTING_STARTED} />;
  }

  // When agreements are outdated, send user to update page instead of showing dashboard (or other content) with modal on top
  if (
    sessionIsValid &&
    greatInitialApiScrapeIsComplete &&
    !userNeedsToBeOnboarded &&
    !allAgreementsCurrent &&
    ![PATHS.ACCOUNT_MANAGEMENT_UPDATE_AGREEMENTS, PATHS.LOG_OUT].includes(
      pathname
    )
  ) {
    return <Redirect to={PATHS.ACCOUNT_MANAGEMENT_UPDATE_AGREEMENTS} />;
  }

  if (!isMemberActive && PAGES_FORBIDDEN_TO_INACTIVE_USERS.includes(pathname)) {
    return <Redirect to={PATHS.LANDING_PAGE} />;
  }

  if (sessionIsValid && PAGES_FORBIDDEN_TO_LOGGED_IN_USERS.includes(pathname)) {
    return <Redirect to={PATHS.LANDING_PAGE} />;
  }
  if (
    greatInitialApiScrapeIsComplete &&
    hasNewResultsToView &&
    !TERMS_AND_CONDITION_PAGES.includes(pathname) &&
    pathname !== PATHS.RESULTS_SLIDES
  ) {
    return <Redirect to={PATHS.RESULTS_SLIDES} />;
  }

  return (
    <>
      <ScrollToTop />
      <Switch>
        <Route exact path={PATHS.LANDING_PAGE}>
          <DashboardPage exact path={PATHS.LANDING_PAGE} />
        </Route>
        {/* Authentication */}
        <Route exact path={PATHS.API_SCRAPE}>
          <ScrapeApiPage {...props} />
        </Route>
        <Route exact path={PATHS.LOGIN}>
          <LoginPage {...props} />
        </Route>
        <Route exact path={PATHS.LOGIN_CALLBACK}>
          <LoginCallbackPage {...props} />
        </Route>
        <Route exact path={PATHS.LOGGED_OUT}>
          <LoggedOutPage {...props} />
        </Route>
        <Route exact path={PATHS.LOG_OUT}>
          <LogoutPage {...props} />
        </Route>
        {/* Onboarding */}
        <Route exact path={PATHS.ONBOARDING_WELCOME}>
          <WelcomePage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_WELCOME_UNPAID_INVITE}>
          <UnpaidInviteWelcomePage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_WELCOME_PAID_INVITE}>
          <PaidInviteWelcomePage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_GENOMIC_LIFE_COMPLETE_PRODUCT}>
          <GenomicLifeCompleteProductPage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_REGISTRATION_INSTRUCTIONS}>
          <RegistrationInstructions {...props} />
        </Route>

        <Route exact path={PATHS.ONBOARDING_GETTING_STARTED}>
          <GettingStartedPage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_TERMS}>
          <TermsPage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_PROFILE}>
          <ProfilePage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_UNPAID_INVITE_PROFILE}>
          <UnpaidInviteProfilePage {...props} />
        </Route>
        <Route exact path={PATHS.ONBOARDING_UNPAID_INVITE_PAYMENT}>
          <UnpaidInvitePaymentPage {...props} />
        </Route>
        {/* Account Management */}
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT}>
          <AccountManagementPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_MEMBER_INFORMATION}>
          <MemberInfoPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_MFA}>
          <MFAPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION}>
          <PlanInformationPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_GIFT_MEMBERSHIP}>
          <GiftMembershipPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_GIFT_RECIPIENTS}>
          <GiftRecipientsPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_GIFT_PAYMENT}>
          <GiftPaymentPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_EXPLANATION_OF_BENEFITS}>
          <ExplanationOfBenefitsPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_MEMBERSHIP_PAYMENT}>
          <MembershipPaymentPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_PAYMENT_RECEIPT}>
          <PaymentReceiptPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_MEMBERSHIP_INVITE}>
          <ShareGenomicLifeInvitePage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_CANCEL_MEMBERSHIP}>
          <CancelMembershipPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_UPDATE_AGREEMENTS}>
          <UpdateAgreementsPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_AND_SECURITY}>
          <PrivacySecurityPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_AGREEMENTS}>
          <TermsOfServicePage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_RESET_PASSWORD}>
          <ChangePasswordPage {...props} />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_POLICY}>
          <AgreementReviewPage
            {...props}
            title="Privacy Policy"
            formId={FORM_IDS.PRIVACY_POLICY}
          />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}>
          <AgreementReviewPage
            {...props}
            title="Terms & Conditions"
            formId={FORM_IDS.TERMS_AND_CONDITIONS}
          />
        </Route>
        <Route exact path={PATHS.ACCOUNT_MANAGEMENT_HIPAA}>
          <AgreementReviewPage
            {...props}
            title="HIPAA Authorization"
            formId={FORM_IDS.HIPAA_AUTHORIZATION}
          />
        </Route>
        {/* Survey Pages */}
        <Route exact path={PATHS.GETTING_STARTED_SURVEY}>
          <PrecisionGuidanceSurveyPage {...props} />
        </Route>
        <Route exact path={PATHS.KIT_REQUEST_SURVEY}>
          <KitRequestSurveyPage {...props} />
        </Route>
        <Route exact path={PATHS.CANCER_SURVEY}>
          <CancerSurveyPage {...props} />
        </Route>
        <Route exact path={PATHS.ORDER_KIT_INFORMED_CONSENT_SURVEY}>
          <OrderKitSurveyPage {...props} />
        </Route>
        {/* Kit Ordering */}
        <Route exact path={PATHS.ORDER_KIT}>
          <OrderingPage
            goBackPath={PATHS.LANDING_PAGE}
            goBackTitle={"Dashboard"}
            {...props}
          />
        </Route>
        <Route exact path={PATHS.ORDER_KIT_SELECTION}>
          <TestKitSelectionPage
            goBackPath={PATHS.LANDING_PAGE}
            goBackTitle={"Dashboard"}
            {...props}
          />
        </Route>
        <Route exact path={PATHS.ORDER_KIT_CONFIRMATION}>
          <OrderKitConclusionPage {...props} />
        </Route>
        {/* Report and Results */}
        <Route exact path={PATHS.RESULTS_SLIDES}>
          <ResultsSlidesPage {...props} />
        </Route>
        <Route exact path={PATHS.RESULTS_MORE_INFO_SLIDE}>
          <ResultsMoreInfoSlidePage {...props} />
        </Route>
        <Route path={`${PATHS.TEST_RESULTS_PAGE}/:glOrderId/:documentId`}>
          <TestResultDocumentPage {...props} />
        </Route>
        <Route path={`${PATHS.TEST_RESULTS_PAGE}/:glOrderId`}>
          <TestResultsPage {...props} />
        </Route>
        {/* CMS Controlled Pages */}
        <Route exact path={PATHS.HELP_CENTER}>
          <HelpCenterPage {...props} />
        </Route>
        {/* Redirects to CMS for legacy links */}
        <Route exact path={PATHS.RESOURCE_CENTER}>
          <Redirect to={"/content/help-center/"} />
        </Route>
        <Route exact path={PATHS.CANCER_RESOURCE_LIBRARY}>
          <Redirect to={"/content/help-center/"} />
        </Route>
        <Route exact path={PATHS.PROGRAM_INFORMATION}>
          <Redirect
            to={"/content/help-center/articles/genomic-life-program-overview/"}
          />
        </Route>
        <Route exact path={PATHS.CANCER_AND_GENOMICS}>
          <Redirect to={"/content/help-center/topics/health-library/"} />
        </Route>
        <Route exact path={PATHS.WHY_GENOMICS}>
          <Redirect to={"/content/help-center/"} />
        </Route>
        <Route exact path={PATHS.CANCER_GUARDIAN_RESOURCE}>
          <Redirect
            to={
              "/content/help-center/topics/benefits-services/cancer-navigation/"
            }
          />
        </Route>
        {/* Utility Pages */}
        <Route exact path={PATHS.MAINTENANCE}>
          <MaintenancePage exact path={PATHS.MAINTENANCE} />
        </Route>
      </Switch>
      <IdleTimer />
    </>
  );
};

export default AppRouter;
